import { MockDatabase, MockRecord, MockTable, TableQuery } from "../MockDatabase";

export type OrganizationAttributionAPI = {
    attribution_guid: string;
    display_name: string;
    status: string;
    type: string;
    created_date: string;
    updated_date: string;
    organization_attribution_guid: string;
    required: boolean;
}

export class MockOrganizationsAttributons extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'organizations_attributions';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            organization_attribution_guid: this.PublicKey({
                fieldName: 'organization_attribution_guid'
            }),
            organization_id: this.ForeignKey({
                fieldName: 'organization_id',
                nullable: false,
                index: true,
                foreignTableName: 'organizations',
                foreignTableFieldName: 'id',
            }),
            attribution_id: this.ForeignKey({
                fieldName: 'attribution_id',
                nullable: false,
                index: true,
                foreignTableName: 'attributions',
                foreignTableFieldName: 'id',
            }),
            required: this.Field({
                type: 'boolean',
                fieldName: 'required',
                nullable: false,
                default: {
                    value: false
                }
            }),
            deleted: this.Field({
                type: 'number',
                fieldName: 'deleted',
                nullable: false,
                default: {
                    value: 0
                }
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
        }
        this.simulatedFields = {
            type: (record: MockRecord) => {
                const attrs = record.getRelated('attributions');
                return attrs.length ? attrs[0].values['type'] : null
            }
        }
    }

    private _transformPayload(db: MockDatabase, values: Record<string, any>) {
        let {
            organization_guid, organizationGuid, organization_id, 
            attribution_guid, attributionGuid, attribution_id, 
            ...data
        } = values;
        if ((organization_guid || organizationGuid) && !organization_id) {
            organization_id = db.resolveGuidToId('organizations', (organization_guid || organizationGuid));
        }
        if ((attribution_guid || attributionGuid) && !attribution_id) {
            attribution_id = db.resolveGuidToId('attributions', (attribution_guid || attributionGuid));
        }
        return {
            ...data,
            ...(attribution_id ? {attribution_id} : {}),
            ...(organization_id ? {organization_id} : {}),
        }
    }

    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        const transFormFields: Record<string, any> = {};
        const remQuery: TableQuery = [];
        query.forEach(entry => {
            if (entry.fieldName === 'organization_guid' || entry.fieldName === 'organizationGuid') {
                transFormFields['organization_guid'] = entry.value;
            } else if (entry.fieldName === 'attribution_guid' || entry.fieldName === 'attributionGuid') {
                transFormFields['attribution_guid'] = entry.value;
            } else {
                remQuery.push(entry);
            }
        });
        const fixed = this._transformPayload(db, transFormFields);
        Object.entries(fixed).forEach(([fieldName, value]) => remQuery.unshift({
            fieldName,
            value
        }));
        return remQuery;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        return this._transformPayload(db, data);
    }

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db, table, values) => {
        return this._transformPayload(db, values);
    }
    
    serialize = (record: MockRecord) => {
        const organizationRecs: MockRecord[] = record.getRelated('organizations');
        const attributionRecs: MockRecord[] = record.getRelated('attributions');
        if (organizationRecs.length !== 1 || attributionRecs.length !== 1) {
            throw new Error(`Data misconfiguration encountered while serializing ${this.name}. Invalid count of related records in tables "organizations" and "attributions"`);
        }
        return {
            attribution_guid: attributionRecs[0].values['attribution_guid'],
            display_name: organizationRecs[0].values['name'],
            status: attributionRecs[0].values['status'],
            type: attributionRecs[0].values['type'],
            created_date: record.values['created_date'],
            updated_date: record.values['updated_date'],
            organization_attribution_guid: record.values['organization_attribution_guid'],
            required: record.values['required'],
        } as OrganizationAttributionAPI;
    }
}